import { ChangeDetectionStrategy, Component, OnInit, Input, ViewEncapsulation, OnChanges, ChangeDetectorRef } from '@angular/core';
import { Information } from '../../../models/information.model';
import { Customer, InformationGroup, MeteringPoint } from '@model';
import { InformationType, UserType } from '@enums';
import * as fromStore from '@core/store';
import * as fromAccountStore from '@appStore/account';
import { Store } from '@ngrx/store';
import { SubSink } from 'subsink';
import { FakeMissingTranslationHandler } from '@ngx-translate/core';
import { BusinessArea } from 'app/models/enums/business-area.enum';

@Component({
    selector: 'app-information-display',
    templateUrl: './information-display.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
    standalone: false
})
export class InformationDisplayComponent implements OnInit, OnChanges {
  @Input() informationGroup: InformationGroup;
  @Input() iconText = 'information.group.title_icon';
  @Input() showHeader = true;
  @Input() showIsRead = false;
  @Input() customers: Customer[];
  @Input() userType: UserType;
  @Input() meteringPoints: MeteringPoint[];
  @Input() businessArea = BusinessArea.All; // If not explicitly set show all information-pages
  @Input() showCard = false;
  
  show = false;
  hidden = false;

  informationTypeEnum = InformationType;
  informationGroupView: InformationGroup;
 
  private subs = new SubSink();
  isImpersonating: any;

  constructor(private store: Store<fromStore.InformationState>,
    private accountStore: Store<fromAccountStore.State>,
    private changeDetectorRef: ChangeDetectorRef) {}

  ngOnInit() {
 
    this.hidden = this.hideContent(this.customers, this.meteringPoints, this.userType);

    this.createLocalView();

    this.subs.sink = this.accountStore.select(fromAccountStore.getUserInfo).subscribe(userInfo => {
      this.isImpersonating = !!userInfo.impersonatedAsName;
    });

    this.setInitialExpandedInformation();
  }

  hideContent(customers: Customer[], meteringPoints: MeteringPoint[], userType: UserType): boolean {

    // Show Information as disabled if no customer is registered on User (not Manager)
    // !isManger && noCustomerRegistered

    // If User is Manager and has no access to any meteringpoints on Customers or no Customers and MeteringPoints
    // !noCustomerRegistered && noAccessToMeteringPoints && isManger

    let isManger = this.userType === UserType.Manager;
    let noAccessToMeteringPoints = this.meteringPoints?.length === 0;
    let noCustomerRegistered = this.customers?.length === 0;

    if (!isManger && noCustomerRegistered) {
      return true;
    }
    if ((!noCustomerRegistered && noAccessToMeteringPoints) || (noCustomerRegistered && noAccessToMeteringPoints) && isManger) {
      return true;
    }
  }

  ngOnChanges() {
    this.createLocalView();
  }
  
  setInitialExpandedInformation() {
    if (this.informationGroup && this.informationGroup.informations.length > 0) {
      // Last item expanded => show as expanded, close others
      let id = -1;
      let lastIndex = this.informationGroupView.informations.map(e => e.expanded).lastIndexOf(true);
      if (lastIndex >= 0) {
        id = this.informationGroupView.informations[lastIndex].id;
      }

      this.informationGroupView.informations.map(e => {
        if (e.expanded && e.id !== id) {
          this.panelItemClosed(e);
        }
      });

      const selectedInformation = this.informationGroupView.informations.find(x => x.id === id);
      if (selectedInformation && !selectedInformation.isRead && !this.isImpersonating) {
        this.store.dispatch(new fromStore.InformationSetAsRead(selectedInformation.id));
      }
    }
  }

  createLocalView() {
    if (this.informationGroup && this.informationGroup.informations.length > 0) {

      this.show = true;

      if(this.businessArea !== BusinessArea.All) {

        // Filter out information based on business area
        let filteredInformations = this.informationGroup.informations.filter(x => x.businessArea === this.businessArea || x.businessArea === BusinessArea.All);
        
        this.informationGroupView = {
          id: this.informationGroup.id,
          key: null,
          title: this.informationGroup.title,
          expanded: this.informationGroup.expanded,
          informations: filteredInformations,
          unReadCount: this.informationGroup.unReadCount
        };
      } else {

        // Only one can be expanded at a time
        this.informationGroupView = {
          id: this.informationGroup.id,
          key: null,
          title: this.informationGroup.title,
          expanded: this.informationGroup.expanded,
          informations: this.informationGroup.informations,
          unReadCount: this.informationGroup.unReadCount
        };
      }
       // Force the view to update
      this.changeDetectorRef.detectChanges();
    }
  }

  panelOpened(informationGroup: InformationGroup) {
    let updatedInformationGroup = {
      ...informationGroup,
      expanded: true
    };

    this.store.dispatch(new fromStore.UpdateGroup(updatedInformationGroup));
  }

  panelClosed(informationGroup: InformationGroup) {
    let updatedInformationGroup = {
      ...informationGroup,
      expanded: false
    };
    this.store.dispatch(new fromStore.UpdateGroup(updatedInformationGroup));
  }

  togglePanelItem(information: Information) {
    if (this.informationGroupView.informations.some(o => o.id === information.id)) {
      const existingExpandedItem = this.informationGroupView.informations.find(x => x.expanded);

      this.informationGroup.informations.map(item => {
        if (item.id === information.id) {
          if (existingExpandedItem && existingExpandedItem.id === information.id) {
            // Close self
            let closeItem = {
              ...existingExpandedItem,
              expanded: false
            };
            this.store.dispatch(new fromStore.Update(closeItem));
          } else {
            // Close other
            if (existingExpandedItem) {
              let closeItem = {
                ...existingExpandedItem,
                expanded: false
              };
              this.store.dispatch(new fromStore.Update(closeItem));
            }

            // Set state on new item
            const panelState = !information.expanded;
            let updatedItem = {
              ...item,
              expanded: panelState
            };
            this.store.dispatch(new fromStore.Update(updatedItem));

            if (panelState && !item.isRead && !this.isImpersonating) {
              this.store.dispatch(new fromStore.InformationSetAsRead(updatedItem.id));
            }}

        } else {
          return item;
        }
      });
    }
  }

  panelItemClosed(information: Information) {
    if (this.informationGroupView.informations.some(o => o.id === information.id)) {
      this.informationGroup.informations.map(item => {
        if (item.id === information.id) {
          let updatedItem = {
            ...item,
            expanded: false
          };
          this.store.dispatch(new fromStore.Update(updatedItem));
        } else {
          return item;
        }
      });
    }
  }
}
